import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NestedArgField from "./NestedArgField";
import { NEUTRAL, RADIUS } from "../../styles/brandTokens";
import {
  descriptorLabel,
  formatArgLabel,
  normalizeFieldDescriptor,
  parseNestedArgs,
  sortEntriesByIndex,
} from "../utils/argumentUtils";
import {
  isUpdatedArgPath,
  joinArgPath,
  normalizeUpdatedArgPaths,
} from "../hitl/hitlArgDiff";

type JsonObject = Record<string, any>;

interface HitlActionParamsEditorProps {
  args: JsonObject | string | null | undefined;
  argTypes?: JsonObject | string | null;
  resolvedArgs?: JsonObject | string | null;
  disabled?: boolean;
  onChange?: (nextArgs: JsonObject) => void;
  fontSizes?: { body: number };
  displayLabelMap?: Record<string, string>;
  isExtension?: boolean;
  groupEmptyValues?: boolean;
  emptyValuesLabel?: string;
  referenceOptions?: Array<{ label: string; value: string }>;
  /** HITL updated argument paths; highlight matching NestedArgField leaves. */
  updatedArgPaths?: string[];
  /** Unwrapped original HITL args for previous-value captions. */
  originalArgs?: Record<string, unknown> | null;
}

function parseObject(value: JsonObject | string | null | undefined): JsonObject {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string") return parseNestedArgs(value);
  return {};
}

function isPlainObject(value: any): value is JsonObject {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isEmptyFieldValue(value: any) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (isPlainObject(value)) return Object.keys(value).length === 0;
  return false;
}

function getUserInputObject(argsObj: JsonObject) {
  const keys = Object.keys(argsObj);
  return keys.length === 1 &&
    keys[0] === "user_input" &&
    isPlainObject(argsObj.user_input)
    ? argsObj.user_input
    : null;
}

const HitlActionParamsEditor: React.FC<HitlActionParamsEditorProps> = ({
  args,
  argTypes,
  resolvedArgs,
  disabled = false,
  onChange,
  fontSizes = { body: 14 },
  displayLabelMap = {},
  isExtension = false,
  groupEmptyValues = false,
  emptyValuesLabel = "Empty values",
  referenceOptions = [],
  updatedArgPaths = [],
  originalArgs = null,
}) => {
  const [showEmptyValues, setShowEmptyValues] = React.useState(false);
  const argsObj = parseObject(args);
  const argTypesObj = parseObject(argTypes || undefined);
  const resolvedArgsObj = parseObject(resolvedArgs || undefined);
  const innerUserInput = getUserInputObject(argsObj);
  const unwrapUserInput = innerUserInput !== null;
  const displaySource = unwrapUserInput ? innerUserInput : argsObj;
  const rootPath = unwrapUserInput ? "user_input" : "";
  const highlightPaths = normalizeUpdatedArgPaths(updatedArgPaths);

  const getArgTypeForKey = (key: string) =>
    unwrapUserInput && isPlainObject(argTypesObj.user_input)
      ? argTypesObj.user_input[key] ?? argTypesObj[key]
      : argTypesObj[key];

  const getResolvedForKey = (key: string) => {
    if (!isPlainObject(resolvedArgsObj)) return undefined;
    if (unwrapUserInput && isPlainObject(resolvedArgsObj.user_input)) {
      return resolvedArgsObj.user_input[key];
    }
    return resolvedArgsObj[key];
  };

  const setArgValue = (key: string, nextValue: any) => {
    if (disabled || !onChange) return;
    if (unwrapUserInput) {
      onChange({
        ...argsObj,
        user_input: {
          ...(innerUserInput || {}),
          [key]: nextValue,
        },
      });
      return;
    }
    onChange({
      ...argsObj,
      [key]: nextValue,
    });
  };

  const rawEntries = isPlainObject(displaySource)
    ? sortEntriesByIndex(Object.entries(displaySource), getArgTypeForKey)
    : [];

  const dependencyValue = (key: string) =>
    rawEntries.find(([entryKey]) => entryKey === key)?.[1];

  const eligibleEntries = rawEntries.filter(([key]) => {
    const descriptor = normalizeFieldDescriptor(getArgTypeForKey(key));
    if (descriptor.depends_on == null) return true;
    return Boolean(dependencyValue(descriptor.depends_on));
  });

  const { primaryEntries, emptyEntries } = eligibleEntries.reduce(
    (acc, entry) => {
      if (!groupEmptyValues) {
        acc.primaryEntries.push(entry);
        return acc;
      }
      const [key, value] = entry;
      const resolvedValue = getResolvedForKey(key);
      const displayValue = resolvedValue !== null && resolvedValue !== undefined ? resolvedValue : value;
      const rawDescriptor = getArgTypeForKey(key);
      const isRequired = Boolean(rawDescriptor && typeof rawDescriptor === "object" && rawDescriptor.required === true);
      if (!isRequired && isEmptyFieldValue(displayValue)) {
        acc.emptyEntries.push(entry);
      } else {
        acc.primaryEntries.push(entry);
      }
      return acc;
    },
    { primaryEntries: [] as Array<[string, any]>, emptyEntries: [] as Array<[string, any]> }
  );

  const emptyHasUpdates = emptyEntries.some(([key]) => {
    const path = joinArgPath(rootPath, key);
    return (
      isUpdatedArgPath(highlightPaths, path) ||
      highlightPaths.some((updatedPath) => updatedPath.startsWith(`${path}.`))
    );
  });

  React.useEffect(() => {
    if (emptyHasUpdates) setShowEmptyValues(true);
  }, [emptyHasUpdates]);

  const renderField = ([key, value]: [string, any]) => {
    const resolvedValue = getResolvedForKey(key);
    const isResolved = resolvedValue !== null && resolvedValue !== undefined;
    const isNestedObject = isPlainObject(value);
    const fieldDisabled = disabled || (!isNestedObject && isResolved);
    const resolvedNestedValue = isResolved && isPlainObject(resolvedValue) ? resolvedValue : undefined;

    return (
      <NestedArgField
        key={key}
        fieldKey={key}
        fieldValue={value}
        fieldPath={key}
        fieldType={getArgTypeForKey(key)}
        isResolved={isResolved}
        disabled={fieldDisabled}
        displayValue={displayLabelMap[key] || descriptorLabel(getArgTypeForKey(key)) || formatArgLabel(key)}
        resolvedValue={isNestedObject ? resolvedNestedValue : resolvedValue}
        onValueChange={(_topLevelKey, updatedValue) => setArgValue(key, updatedValue)}
        fontSizes={fontSizes}
        isExtension={isExtension}
        getDependencyValue={dependencyValue}
        referenceOptions={referenceOptions}
        valuePath={joinArgPath(rootPath, key)}
        updatedArgPaths={highlightPaths}
        originalArgs={originalArgs}
      />
    );
  };

  if (eligibleEntries.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: fontSizes.body }}>
        No parameters.
      </Typography>
    );
  }

  // Warnings the tool attached to this call (e.g. "the customer may see this note"): `_warnings` on the arg types.
  const warnings = Array.isArray(argTypesObj._warnings)
    ? argTypesObj._warnings.filter((w: unknown): w is string => typeof w === "string" && w.trim() !== "")
    : [];

  return (
    <Box width="100%">
      {warnings.map((warning: string) => (
        <Alert key={warning} severity="warning" sx={{ mb: 1, fontFamily: "var(--theo-font-sans)", fontSize: fontSizes.body }}>
          {warning}
        </Alert>
      ))}
      {primaryEntries.map(renderField)}
      {groupEmptyValues && emptyEntries.length > 0 ? (
        <Accordion
          expanded={showEmptyValues}
          onChange={() => setShowEmptyValues((current) => !current)}
          disableGutters
          sx={{
            mt: 1,
            borderRadius: `${RADIUS.control}px !important`,
            border: `1px solid ${NEUTRAL.border}`,
            backgroundColor: "#FFFBF8",
            boxShadow: "none",
            overflow: "hidden",
            "&.Mui-expanded": {
              margin: 0,
              mt: 1,
            },
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: NEUTRAL.textSecondary, fontSize: 20 }} />}
            sx={{
              minHeight: "40px !important",
              backgroundColor: "#FFFBF8",
              px: 1.5,
              "& .MuiAccordionSummary-content": { margin: "8px 0 !important" },
            }}
          >
            <Typography
              fontSize={fontSizes.body}
              fontWeight={500}
              color={NEUTRAL.textSecondary}
              fontFamily="var(--theo-font-sans)"
            >
              {`${emptyValuesLabel} (${emptyEntries.length})`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              p: fontSizes.body <= 13 ? 1 : 2,
              pt: 1,
              backgroundColor: "#FFFBF8",
              borderTop: showEmptyValues ? `1px solid ${NEUTRAL.border}` : "none",
            }}
          >
            {emptyEntries.map(renderField)}
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Box>
  );
};

export default HitlActionParamsEditor;
